import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import SuperAdminSidebar from './SuperAdminSidebar';
import BloggerSidebar from '../Blogger/BloggerSidebar';
import '../../styles/CreateBlog.css';
import { FaSave, FaTimes, FaArrowLeft } from 'react-icons/fa';

const CreateBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const quillRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editingImageElement, setEditingImageElement] = useState(null);
  const [imageModalData, setImageModalData] = useState({
    url: '',
    width: 600,
    height: 400,
    originalWidth: 600,
    originalHeight: 400,
    align: 'center', // new: alignment
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    category: 'General',
    tags: '',
    status: 'published',
    featuredImage: ''
  });

  // Register a custom image blot so width/height attributes are stored in delta
  useEffect(() => {
    try {
      const ImageBlot = Quill.import('formats/image');
      class CustomImage extends ImageBlot {
        static create(value) {
          const node = super.create(value.src || value);
          if (typeof value === 'object') {
            if (value.width) node.setAttribute('width', value.width);
            if (value.height) node.setAttribute('height', value.height);
            if (value.style) node.setAttribute('style', value.style);
          }
          return node;
        }

        static value(node) {
          return {
            src: node.getAttribute('src'),
            width: node.getAttribute('width'),
            height: node.getAttribute('height')
          };
        }
      }

      Quill.register(CustomImage, true);
    } catch (err) {
      // If Quill isn't available yet or registration fails, ignore silently
      console.warn('CustomImage blot registration skipped:', err?.message || err);
    }
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (id) {
      fetchBlog(id);
    }
  }, [id]);

  const fetchBlog = async (blogId) => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${API_BASE_URL}/api/blogs/${blogId}`);
      if (response.data.success) {
        const blog = response.data.data;
        setFormData({
          title: blog.title,
          description: blog.description,
          content: blog.content,
          category: blog.category,
          tags: blog.tags.join(', '),
          status: blog.status,
          featuredImage: blog.featuredImage || ''
        });
        setEditMode(true);
      }
    } catch (err) {
      setError('Failed to load blog');
      console.error('Error fetching blog:', err);
    } finally {
      setLoading(false);
    }
  };

  // Attach a single persistent click listener on the editor root for both create and edit modes
  useEffect(() => {
    let attachedEditor = null;
    let handleEditorClick = null;

    const attach = () => {
      try {
        if (!quillRef.current || !quillRef.current.getEditor) {
          // try again shortly until Quill is ready
          setTimeout(attach, 150);
          return;
        }

        const editor = quillRef.current.getEditor();
        if (!editor || !editor.root) return;

        attachedEditor = editor;

        handleEditorClick = (e) => {
          const target = e.target;
          if (target && target.tagName === 'IMG') {
            e.stopPropagation();

            const src = target.src;

            // Robust dimension detection: prefer DOM properties, then inline style, then defaults
            const width = target.width || target.naturalWidth || parseInt(target.style.width) || 600;
            const height = target.height || target.naturalHeight || parseInt(target.style.height) || 400;

            // Load original image dimensions if available
            const originalImg = new Image();
            originalImg.onload = () => {
              setImageModalData({
                url: src,
                width: width,
                height: height,
                originalWidth: originalImg.width || width,
                originalHeight: originalImg.height || height,
                align: (target.style.float === 'left' ? 'left' : target.style.float === 'right' ? 'right' : (target.style.display === 'block' && target.style.margin === '0 auto' ? 'center' : 'center'))
              });
              setEditingImageElement(target);
              setShowImageModal(true);
            };
            originalImg.src = src;
          }
        };

        editor.root.addEventListener('click', handleEditorClick);
      } catch (err) {
        // ignore and retry
        setTimeout(attach, 200);
      }
    };

    attach();

    return () => {
      try {
        if (attachedEditor && handleEditorClick) {
          attachedEditor.root.removeEventListener('click', handleEditorClick);
        }
      } catch (err) {}
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          featuredImage: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQuillImageUploadCallback = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Image = reader.result;
          
          // Get image dimensions
          const img = new Image();
          img.onload = () => {
            setImageModalData({
              url: base64Image,
              width: 600,
              height: Math.round((600 * img.height) / img.width),
              originalWidth: img.width,
              originalHeight: img.height
            });
            setShowImageModal(true);
          };
          img.src = base64Image;
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'font': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['link', 'image', 'video']
      ],
      handlers: {
        image: handleQuillImageUploadCallback
      }
    }
  }), [handleQuillImageUploadCallback]);

  const insertImageIntoEditor = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const editorElement = editor.container.querySelector('.ql-editor');
      if (!editorElement) return;

      // Alignment style
      let alignStyle = '';
      if (imageModalData.align === 'left') {
        alignStyle = `float: left; margin: 15px 24px 15px 0; display: inline-block;`;
      } else if (imageModalData.align === 'right') {
        alignStyle = `float: right; margin: 15px 0 15px 24px; display: inline-block;`;
      } else {
        alignStyle = `display: block; margin: 15px auto;`;
      }
      const styleString = `width: ${imageModalData.width}px; height: ${imageModalData.height}px; border-radius: 4px; ${alignStyle}`;

      // Helper to find image embed index in Quill delta by src
      const findImageIndexBySrc = (ed, src) => {
        const delta = ed.getContents();
        let idx = 0;
        for (const op of delta.ops) {
          if (op.insert) {
            if (typeof op.insert === 'string') {
              idx += op.insert.length;
            } else if (op.insert.image) {
              const imageVal = op.insert.image;
              if ((typeof imageVal === 'string' && imageVal === src) || (typeof imageVal === 'object' && imageVal.src === src)) return idx;
              idx += 1; // image occupies length 1
            } else {
              // other embeds
              idx += 1;
            }
          }
        }
        return -1;
      };

      if (editingImageElement) {
        // Replace existing image at its index using Quill API
        const index = findImageIndexBySrc(editor, imageModalData.url);
        if (index >= 0) {
          // delete and re-insert so quill updates history/delta cleanly
          editor.deleteText(index, 1, 'user');
          editor.insertEmbed(index, 'image', { src: imageModalData.url, width: imageModalData.width, height: imageModalData.height, style: styleString }, 'user');
        } else {
          // fallback: try to insert at current selection
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', { src: imageModalData.url, width: imageModalData.width, height: imageModalData.height, style: styleString }, 'user');
        }
      } else {
        const range = editor.getSelection(true) || { index: editor.getLength() };
        editor.insertEmbed(range.index, 'image', { src: imageModalData.url, width: imageModalData.width, height: imageModalData.height, style: styleString }, 'user');
      }

      // After Quill updates DOM, set inline style to control width/height/alignment
      setTimeout(() => {
        const images = editorElement.querySelectorAll('img');
        let updated = false;
        images.forEach(img => {
          // match by src; for data-urls be exact
          if (img.src === imageModalData.url) {
            img.setAttribute('width', imageModalData.width);
            img.setAttribute('height', imageModalData.height);
            img.setAttribute('style', styleString);
            updated = true;
          }
        });
        if (updated) {
          handleContentChange(editor.root.innerHTML);
        }
      }, 50);
    }

    setShowImageModal(false);
    setEditingImageElement(null);
    setImageModalData({
      url: '',
      width: 600,
      height: 400,
      originalWidth: 600,
      originalHeight: 400,
      align: 'center',
    });
  };
      
  const deleteImageFromEditor = () => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    if (!editor) return;

    const findImageIndexBySrc = (ed, src) => {
      const delta = ed.getContents();
      let idx = 0;
      for (const op of delta.ops) {
        if (op.insert) {
          if (typeof op.insert === 'string') {
            idx += op.insert.length;
          } else if (op.insert.image) {
            const imageVal = op.insert.image;
            if ((typeof imageVal === 'string' && imageVal === src) || (typeof imageVal === 'object' && imageVal.src === src)) return idx;
            idx += 1;
          } else {
            idx += 1;
          }
        }
      }
      return -1;
    };

    const index = findImageIndexBySrc(editor, imageModalData.url);
    if (index >= 0) {
      editor.deleteText(index, 1, 'user');
      handleContentChange(editor.root.innerHTML);
    }

    setShowImageModal(false);
    setEditingImageElement(null);
    setImageModalData({ url: '', width: 600, height: 400, originalWidth: 600, originalHeight: 400 });
  };

  // Update content HTML with image styles

  const handleImageDimensionChange = (dimension, value) => {
    const numValue = parseInt(value) || 0;
    
    if (dimension === 'width') {
      const aspectRatio = imageModalData.originalHeight / imageModalData.originalWidth;
      setImageModalData(prev => ({
        ...prev,
        width: numValue,
        height: Math.round(numValue * aspectRatio)
      }));
    } else if (dimension === 'height') {
      const aspectRatio = imageModalData.originalWidth / imageModalData.originalHeight;
      setImageModalData(prev => ({
        ...prev,
        height: numValue,
        width: Math.round(numValue * aspectRatio)
      }));
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Blog title is required');
      return false;
    }
    if (formData.title.trim().length < 5) {
      setError('Title should be at least 5 characters');
      return false;
    }
    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      setError('Blog content is required');
      return false;
    }
    if (formData.content.replace(/<[^>]*>/g, '').trim().length < 10) {
      setError('Content should have at least 10 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        content: formData.content,
        category: formData.category,
        tags: formData.tags
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0),
        status: formData.status,
        featuredImage: formData.featuredImage
      };

      let response;
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      if (editMode && id) {
        response = await axios.put(
          `${API_BASE_URL}/api/blogs/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token')}`
            }
          }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/blogs/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token')}`
            }
          }
        );
      }

      if (response.data.success) {
        navigate(user?.role === 'blogger' ? '/blogger/blogs' : '/super-admin/blogs', {
          state: { message: editMode ? 'Blog updated successfully!' : 'Blog created successfully!' }
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving blog');
      console.error('Error saving blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'code-block', 'list', 'script', 'indent', 'size', 'color',
    'background', 'font', 'align', 'link', 'image', 'video'
  ];

  return (
    <div className="create-blog-wrapper">
      {user?.role === 'blogger' ? <BloggerSidebar /> : <SuperAdminSidebar user={{}} />}
      <div className="create-blog-container">
        <div className="blog-header">
          <button className="back-btn" onClick={() => navigate(user?.role === 'blogger' ? '/blogger/blogs' : '/super-admin/blogs')}>
            <FaArrowLeft /> Back to Blogs
          </button>
          <h1>{editMode ? 'Edit Blog' : 'Create New Blog'}</h1>
        </div>

      {error && <div className="error-alert">{error}</div>}

      <form onSubmit={handleSubmit} className="blog-form">
        <div className="form-section">
          <h2>Blog Details</h2>

          <div className="form-group">
            <label htmlFor="title">Blog Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter blog title..."
              className="form-input"
              maxLength="200"
            />
            <small className="char-count">{formData.title.length}/200</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter a short description..."
              className="form-textarea"
              rows="3"
              maxLength="500"
            />
            <small className="char-count">{formData.description.length}/500</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="Technology">Technology</option>
                <option value="Business">Business</option>
                <option value="Asset Management">Asset Management</option>
                <option value="Maintenance">Maintenance</option>
                <option value="General">General</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags (comma separated)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="e.g., asset management, maintenance, tips"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="featuredImage">Featured Image (Optional)</label>
            <input
              type="file"
              id="featuredImage"
              accept="image/*"
              onChange={handleImageUpload}
              className="form-file"
            />
            {formData.featuredImage && (
              <div className="image-preview">
                <img src={formData.featuredImage} alt="Featured" />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, featuredImage: '' }))}
                  className="remove-image-btn"
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>Blog Content *</h2>
          <div className="editor-toolbar-info">
            <p>📝 Content Guidelines:</p>
            <ul>
              <li><strong>Bold, Italic, Underline</strong> - Text formatting</li>
              <li><strong>Headers</strong> - Structure your content</li>
              <li><strong>Lists</strong> - Create bullet and numbered lists</li>
              <li><strong>Colors & Fonts</strong> - Customize appearance</li>
              <li><strong>Images</strong> - Click image button to add & resize images</li>
            </ul>
          </div>
          <div className="image-size-info">
            <p>💡 <strong>Recommended Image Size:</strong> 600px width × 400px height (or 16:10 aspect ratio) for best results. Images will automatically adjust to fit any screen size.</p>
          </div>
          <div className="rich-editor-wrapper">
            {/* Figma-style Vertical Sidebar Toolbar */}
            <aside className="figma-toolbar-sidebar">
              <div className="sidebar-section">
                <div className="sidebar-section-title">Position</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-align" value="" title="Align Left">⯇</button>
                  <button type="button" className="ql-align" value="center" title="Align Center">≡</button>
                  <button type="button" className="ql-align" value="right" title="Align Right">⯈</button>
                  <button type="button" className="ql-align" value="justify" title="Justify">☰</button>
                </div>
                <div className="sidebar-row sidebar-input-row">
                  <input type="number" min="0" placeholder="X" className="sidebar-input" disabled />
                  <input type="number" min="0" placeholder="Y" className="sidebar-input" disabled />
                </div>
                <div className="sidebar-row sidebar-input-row">
                  <input type="number" min="0" placeholder="0" className="sidebar-input" disabled />
                  <button type="button" className="sidebar-icon-btn" disabled>⤡</button>
                  <button type="button" className="sidebar-icon-btn" disabled>⤢</button>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="sidebar-section-title">Layout</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-list" value="ordered" title="Numbered List">1.</button>
                  <button type="button" className="ql-list" value="bullet" title="Bullet List">•</button>
                  <button type="button" className="ql-indent" value="-1" title="Decrease Indent">←</button>
                  <button type="button" className="ql-indent" value="+1" title="Increase Indent">→</button>
                </div>
                <div className="sidebar-row sidebar-input-row">
                  <input type="number" min="100" max="1200" placeholder="W 600" className="sidebar-input" disabled />
                  <input type="number" min="100" max="800" placeholder="H 400" className="sidebar-input" disabled />
                </div>
                <div className="sidebar-row sidebar-checkbox-row">
                  <input type="checkbox" checked readOnly /> <span>Clip content</span>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="sidebar-section-title">Appearance</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-bold" title="Bold"><b>B</b></button>
                  <button type="button" className="ql-italic" title="Italic"><i>I</i></button>
                  <button type="button" className="ql-underline" title="Underline"><u>U</u></button>
                  <button type="button" className="ql-strike" title="Strikethrough"><s>S</s></button>
                </div>
                <div className="sidebar-row">
                  <button type="button" className="ql-color" title="Text Color">A</button>
                  <button type="button" className="ql-background" title="Background Color">🖌️</button>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="sidebar-section-title">Font</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-header" value="1" title="Heading 1">H1</button>
                  <button type="button" className="ql-header" value="2" title="Heading 2">H2</button>
                  <button type="button" className="ql-header" value="3" title="Heading 3">H3</button>
                  <button type="button" className="ql-header" value="4" title="Heading 4">H4</button>
                </div>
                <div className="sidebar-row">
                  <button type="button" className="ql-font" value="sans-serif" title="Sans Serif">Aa</button>
                  <button type="button" className="ql-font" value="serif" title="Serif">Aa</button>
                  <button type="button" className="ql-font" value="monospace" title="Mono">Aa</button>
                  <button type="button" className="ql-size" value="small" title="Small">A-</button>
                  <button type="button" className="ql-size" value="large" title="Large">A+</button>
                  <button type="button" className="ql-size" value="huge" title="Huge">A++</button>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="sidebar-section-title">Blocks</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-blockquote" title="Blockquote">❝</button>
                  <button type="button" className="ql-code-block" title="Code Block">`</button>
                  <button type="button" className="ql-script" value="sub" title="Subscript">x₂</button>
                  <button type="button" className="ql-script" value="super" title="Superscript">x²</button>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="sidebar-section-title">Insert</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-link" title="Insert Link">🔗</button>
                  <button type="button" className="ql-image" title="Insert Image">🖼️</button>
                  <button type="button" className="ql-video" title="Insert Video">🎥</button>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="sidebar-section-title">Other</div>
                <div className="sidebar-row">
                  <button type="button" className="ql-clean" title="Remove Formatting">🧹</button>
                </div>
              </div>
            </aside>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formData.content}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              placeholder="Write your blog content here..."
              className="rich-editor"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => navigate(user?.role === 'blogger' ? '/blogger/blogs' : '/super-admin/blogs')}
            disabled={loading}
          >
            <FaTimes /> Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={loading}
          >
            <FaSave /> {loading ? 'Saving...' : (editMode ? 'Update Blog' : 'Create Blog')}
          </button>
        </div>
      </form>

      {/* Image Resize Modal */}
      {showImageModal && (
        <div className="image-modal-overlay">
          <div className="image-modal">
            <div className="image-modal-header">
              <h3>{editingImageElement ? 'Edit Image Size' : 'Resize Image'}</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowImageModal(false);
                  setEditingImageElement(null);
                }}
                type="button"
              >
                <FaTimes />
              </button>
            </div>

            <div className="image-modal-content">
              <div className="image-preview-large">
                <img
                  key={`${imageModalData.url}-${imageModalData.width}-${imageModalData.height}`}
                  src={imageModalData.url}
                  alt="Resize preview"
                  style={{
                    width: imageModalData.width + 'px',
                    height: imageModalData.height + 'px',
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    display: 'block',
                    marginLeft: imageModalData.alignment === 'center' ? 'auto' : imageModalData.alignment === 'right' ? 'auto' : 0,
                    marginRight: imageModalData.alignment === 'center' ? 'auto' : imageModalData.alignment === 'left' ? 'auto' : 0,
                    float: imageModalData.alignment === 'left' ? 'left' : imageModalData.alignment === 'right' ? 'right' : 'none',
                  }}
                />
              </div>

              {/* Image Alignment Controls */}
              <div className="image-align-controls">
                <label>Alignment:</label>
                <button
                  type="button"
                  className={`align-btn${imageModalData.alignment === 'left' ? ' align-active' : ''}`}
                  onClick={() => setImageModalData(prev => ({ ...prev, alignment: 'left' }))}
                >
                  Left
                </button>
                <button
                  type="button"
                  className={`align-btn${imageModalData.alignment === 'center' ? ' align-active' : ''}`}
                  onClick={() => setImageModalData(prev => ({ ...prev, alignment: 'center' }))}
                >
                  Center
                </button>
                <button
                  type="button"
                  className={`align-btn${imageModalData.alignment === 'right' ? ' align-active' : ''}`}
                  onClick={() => setImageModalData(prev => ({ ...prev, alignment: 'right' }))}
                >
                  Right
                </button>
              </div>

              <div className="image-sizing-controls">
                <div className="size-input-group">
                  <label htmlFor="image-width">Width (px)</label>
                  <input
                    type="number"
                    id="image-width"
                    value={imageModalData.width}
                    onChange={(e) => handleImageDimensionChange('width', e.target.value)}
                    min="100"
                    max="1200"
                    className="size-input"
                  />
                </div>

                <div className="size-input-group">
                  <label htmlFor="image-height">Height (px)</label>
                  <input
                    type="number"
                    id="image-height"
                    value={imageModalData.height}
                    onChange={(e) => handleImageDimensionChange('height', e.target.value)}
                    min="100"
                    max="800"
                    className="size-input"
                  />
                </div>

                <div className="size-info">
                  <p><strong>Aspect Ratio Locked:</strong> Maintains original image proportions</p>
                  <p><strong>Current Size:</strong> {imageModalData.width}px × {imageModalData.height}px</p>
                </div>

                <div className="preset-sizes">
                  <p><strong>Quick Presets:</strong></p>
                  <div className="preset-buttons">
                    <button
                      type="button"
                      onClick={() => {
                        const h = Math.round((400 * imageModalData.originalHeight) / imageModalData.originalWidth);
                        setImageModalData(prev => ({
                          ...prev,
                          width: 400,
                          height: h
                        }));
                      }}
                      className={`preset-btn ${imageModalData.width === 400 ? 'preset-active' : ''}`}
                    >
                      Small (400px)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const h = Math.round((600 * imageModalData.originalHeight) / imageModalData.originalWidth);
                        setImageModalData(prev => ({
                          ...prev,
                          width: 600,
                          height: h
                        }));
                      }}
                      className={`preset-btn ${imageModalData.width === 600 ? 'preset-active' : ''}`}
                    >
                      Medium (600px)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const h = Math.round((800 * imageModalData.originalHeight) / imageModalData.originalWidth);
                        setImageModalData(prev => ({
                          ...prev,
                          width: 800,
                          height: h
                        }));
                      }}
                      className={`preset-btn ${imageModalData.width === 800 ? 'preset-active' : ''}`}
                    >
                      Large (800px)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="image-modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowImageModal(false)}
              >
                Cancel
              </button>
              {editingImageElement && (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={deleteImageFromEditor}
                >
                  Delete Image
                </button>
              )}
              <button
                type="button"
                className="btn-submit"
                onClick={insertImageIntoEditor}
              >
                Insert Image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default CreateBlog;
